/** @jsx jsx */
import type { PointerEventHandler, ReactNode } from 'react';
import { css, cssMap, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { Position } from '../../common/types.tsx';

type ResizeHandleProps = {
	children?: ReactNode;
	collapsed?: boolean;
	onPointerDown?: PointerEventHandler;
	resizeHandleAsOverlay?: boolean;
	resizeHandleZIndex?: number;
	position?: Position;
};

const baseStyles = css({
	alignSelf: 'stretch',
	display: 'flex',
	flexGrow: 0,
	flexShrink: 0,
	justifyContent: 'center',
	right: 0,
	width: token('space.300'),
	'&:focus': {
		outline: 'none',
	},
});

const collapsedStyles = css({
	cursor: 'pointer',
	marginRight: token('space.300'),
});

const nonCollapsedStyles = css({
	cursor: 'ew-resize',
});

const positionStyles = cssMap({
	left: {
		left: 0,
	},
	right: {
		right: 0,
	},
});

const resizeStyles = cssMap({
	base: {
		height: '100%',
		position: 'absolute',
		top: 0,
	},
	left: {
		transform: 'translate3d(-50%, 0, 0)',
	},
	right: {
		transform: 'translate3d(50%, 0, 0)',
	},
});

const scrollFixStyles = css({
	position: 'relative',
});

export const ResizeHandle = ({
	children,
	collapsed = false,
	onPointerDown,
	resizeHandleAsOverlay = false,
	resizeHandleZIndex,
	position = 'right',
}: ResizeHandleProps) => (
	<div
		css={[
			baseStyles,
			collapsed ? collapsedStyles : nonCollapsedStyles,
			positionStyles[position],
			resizeHandleAsOverlay && resizeStyles.base,
			resizeHandleAsOverlay && resizeStyles[position],
			!resizeHandleAsOverlay &&
				expVal('sunsetting_bulk_edit', 'isEnabled', false) &&
				scrollFixStyles,
		]}
		data-component-selector="jira-issue-view-common-component-resize-handle"
		data-testid="flex-resizer.ui.handle.resizer"
		onPointerDown={onPointerDown}
		// eslint-disable-next-line jira/react/no-style-attribute
		style={{ zIndex: resizeHandleZIndex }}
	>
		{children}
	</div>
);
